import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { Map, TileLayer, LatLng } from 'leaflet';
import { DataLayer } from './data-layer';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { AppConfigService } from './app-config.service';

// declare all javascript variables/functions defined elsewhere
declare var defaultSettings: any;
declare var L;


declare var require: any;

@Injectable()
export class MapService {
	map: Map;
	
	// galleria
	// galleria images
	images: any[] = [];
	// galleria boolean to display or not
	displayImages = false;
	// active index - used to force galleria to update image
	activeIndex = 0;
	responsiveOptions:any[] = [
		{
			breakpoint: '1024px',
			numVisible: 5
		},
		{
			breakpoint: '768px',
			numVisible: 3
		},
		{
			breakpoint: '560px',
			numVisible: 1
		}
	]

	http: HttpClient;
	systemConfiguration: any;
	userPreferences;
	baseDir: string;
	baseDataDir: string;
	baseMaps: any;
	dataLayerService: DataLayerService;
	configService: ConfigService;
	activeTileLayer: TileLayer;
	preferencesObservable;

	// stores legends of additional layers ids and display status
	dataLayerLegendDisplay: any[] = [];		

	// track the user's preference for showing legend.
	showLegend: boolean;					

	// default value in case user preferences doesn't specify
	currentCenter: LatLng = new L.LatLng( 
		defaultSettings.map.latitude, 
		defaultSettings.map.longitude);

	// default value in case user preferences doesn't specify
	currentZoom: number = defaultSettings.map.zoom;

	// zoom levels listed in the dropdown on the main page
	bboxArray: any[] = [];
	zoomSelection = 'blank';

	// tracks the number of overlay layers currently active;
	// if 0, don't show the overlay legend dialog
	dataLayerLegendCount = 0; 
	dataLayerLegendCountObservable: Subject<any>;
	dataLayerLegendCountObserver;	

	// tmpDataLayer is only used to communicate dataLayer to placeMarkers
	tmpDataLayer;

	constructor( 
		public appConfigService: AppConfigService,
		configService: ConfigService,
		dataLayerService: DataLayerService, 
		http: HttpClient ) {

		this.configService = configService;
		this.dataLayerService = dataLayerService;
		this.http = http;

		this.dataLayerLegendCountObservable = new Subject<any>();		// tracks the number of overlay layers currently active; if 0, don't show the overlay legend dialog
		this.dataLayerLegendCountObserver = this.dataLayerLegendCountObservable.asObservable();;

		this.setSystemConfiguration();
		this.mapSetUp ();
		this.setBasicZoomOptions();
		this.setZoomAroundUserPreferences( );

		this.preferencesObservable = this.configService.getPreferencesObservable().subscribe ({
			next: (p) => {
				console.log("Reacting to preference change.");
				// console.log(p.preferences.map.baseMap);
				this.reactToPreferenceChange(p);
			}
			}
		);
	}

	reactToPreferenceChange(p) {
		console.log(p);
		if ( p != undefined ) {
			this.updateUserPreferences(p);
			this.setZoomAroundUserPreferences();
			this.setBasicZoomOptions();

			// user prefers different baseMap?
			if ( p.preferences?.map?.baseMap != null ) {
				if ( p.preferences.map.baseMap != this.getActiveTileLayerName() ) {
					if ( this.map != null ) {
						this.map.removeLayer(this.activeTileLayer);
					}
					this.setActiveTileLayer( this.baseMaps[p.preferences.map.baseMap]);
				}
			} else { console.log("baseMap was null");}
		}
	}

	doDisplayImages(images) {
		// we have to trick Galleria into thinking
		// image index has changed.
		const self = this;
		this.activeIndex++;
		setTimeout(function(){
				console.log('In setTimeout function');
				self.activeIndex = 0;
				self.displayImages = true;
		}, 100);
	}

	// Set up the map default values
	setSystemConfiguration ( ) {
		this.baseDataDir = this.appConfigService.getBaseDataDir();
		this.baseDir = this.appConfigService.getBaseDir();
	}

	// respond to user settings if it contains baseMap configuration
	// otherwise set basemap to default
	mapSetUp () {
		const config = this.appConfigService.getOperatorInterfaceConfiguration();
		const baseMaps = config?.baseMaps || defaultSettings.map.defaultBaseMaps;

		this.baseMaps = this.createBaseMaps(baseMaps);
		// console.log(this.baseMaps);
		// console.log(this.defaultBaseMap);

		let baseMapName = this.configService.getUserPreferences().preferences?.map?.baseMap;

		if ( baseMapName == undefined ) {
			baseMapName = defaultSettings.map.baseMap; 
		}

		console.log("Setting basemap: ", baseMapName);
		this.showLegend = false;
		this.activeTileLayer = this.baseMaps[baseMapName];
	}

	updateUserPreferences ( userPreferences ) {
		// console.log(this.userPreferences);
		if ( userPreferences != null ) {
			this.userPreferences = userPreferences;
		}
	}

	// sets the basic zoom options to be used on the main
	// application
	setBasicZoomOptions() {
		this.bboxArray = [{
			label: ' ',
			bbox: 'blank'
		}, {
			label: 'Zoom to default',
			bbox: 'default'
		}];

		if (this.configService.isARealUser() == true) {
			// console.log(this.configService.isARealUser());
			this.bboxArray.push ( {
				label: 'Zoom to user preferences',
				bbox: 'user'
			});
		}
	}

	// zoom options include default values plus
	// bbox values associated with the current layer
	addZoomOptions(stations) {
			// not written yet - other than layer zoom
			// options, what else might be useful?
	}

	addLayerZoomOptions(layerId) {
		const dataLayer = this.dataLayerService.getDataLayerDetails(layerId);
		// console.log ( dataLayer );
		const stations = dataLayer.value.features;
		const station_id = dataLayer.stationID;
		const station_name = dataLayer.stationName;
		for ( const i in stations ) {
			let newStation: any;
			if ( stations[i].bbox != null) {
				newStation = { id: stations[i].properties[station_id],
					name: stations[i].properties[station_name],
					bbox: stations[i].bbox};

				this.bboxArray.push(newStation);
				// console.log(stations[i].properties);
			}
		}
	}

	resetZoomSelection() {
		this.zoomSelection = 'blank';
	}

	getZoomOptions() {
		return this.bboxArray;
	}

	// tslint:disable-next-line:max-line-length
	/* Creates the baseMaps object based on a json object.  Expects an array of json objects.  Returns a single json object with layer definitions.
	[
			{
				"name" : "GoogleSatellite",
				"layerURL": 'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
				"options": {
						subdomains:['mt0','mt1','mt2','mt3'],
						attribution: '<a href="https://maps.google.com/earth">Map data: Google Satellite</a>'
					}
			}, { additional map layer definitions }
	]
	Returns:
	{
            GoogleSatellite: new L.TileLayer('http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',{
				subdomains:['mt0','mt1','mt2','mt3'],
				attribution: '<a href="https://maps.google.com/earth">Map data: Google Satellite</a>'
			}), {...} ;
	*/
	createBaseMaps ( definitions ) {
		const self = this;
		const baseMaps = {};
		definitions.forEach(function(item) {
			let basemapURL = item.layerURL;
			basemapURL = basemapURL.replace( '%external-data%', self.baseDir);
			// console.log('Adding basemap ' + item.name + ', ' + basemapURL);
			baseMaps[item.name] = new L.TileLayer(basemapURL, item.options);
		});
		return baseMaps;
	}

	/*
	** Allow user-defined (custom) baseMaps.
	** This does the exact same thing except it adds to baseMaps rather than returning the new definitions.
	*/
	addBaseMaps ( definitions ) {
		const self = this;
		definitions.forEach(function(item) {
			console.log(item);
			self.baseMaps[item.name] = new L.TileLayer(item.layerURL, item.options);
		});
	}

	// When the user clicks on another route and then returns to map, we need to refresh all the active layers.
	// Passing context for the foreach function was just a little tricky.
	// See http://stackoverflow.com/questions/22814097/how-to-pass-context-to-foreach-anonymous-function
	refreshAllLayers() {
		this.dataLayerService.getDataLayers().then(dataLayers => {
			// console.log(this);
			// console.log(dataLayers);
			dataLayers.forEach(function(item) {
				// console.log('In map.service.refreshAllLayers ' + item.dataType + ' ' + item.active );
				if ( item.dataType === 'dynamicOverlayLayer') {
					(item.component.instance).refreshLayer();
				} else if (item.active === true) {
					this.activateLayer(item.dataType);
				}
			}, this);
		});
	}

	// this is the opposite of refreshAllLayers.  All layers are removed from the map since leaflet doesn't
	// seem to garbage collect these layers, but the active flag remains untouched.  This way, when the map
	// component is selected, the active layers will still be active and redisplayed.
	removeAllLayers() {
		this.dataLayerService.getDataLayers().then(dataLayers => {
			// console.log(this);
			// console.log(dataLayers);
			dataLayers.forEach(function(item) {
				// console.log("In map.service.removeAllLayers " + item.dataType + " " + item.active )
				if (item.active === true) {
					this.map.removeLayer(item.mapLayer);
				}
			}, this);
		});
	}

	// Given a data type, the dataLayerService retrieves the appropriate data which is then added to the map.
	// The active flag is also set to true.
	activateLayer(dataType: string, autoRefresh: boolean) {
		const dataLayer = this.dataLayerService.getDataLayerDetails(dataType);
		// console.log(dataType);

		dataLayer.active = true;

		// setting tmpDataLayer for placeMarkers
		this.tmpDataLayer = dataLayer;

		if ( this.map != null && dataLayer.value != null ) {
			dataLayer.mapLayer = L.geoJson(dataLayer.value, {
				// placeMarkers using configuration which specifies which property should be used as the title, popup, etc.
				pointToLayer: this.placeMarkers,
				onEachFeature: this.onEachPointFeature,
				contents: {'dataLayer': dataLayer, 'self': this }
			}, this).addTo(this.map);

			// after a layer has been activated, filter the stations if a search string has been specified by the user
			if ( !autoRefresh ) {
				this.dataLayerService.triggerMapFilterEvent(dataType);
			}
		}
	}

	//
	// onEachPointFeature controls the popup content.
	//
	onEachPointFeature(feature, layer) {
		// console.log(feature);
		// const dataLayer = this['contents'];
		const contents = this['contents'];
		const dataLayer = contents.dataLayer;
		const self = contents.self;

		// console.log(this)
		var stationName = dataLayer.stationName;
		var stationID = dataLayer.stationID;
		var capacity = dataLayer.capacity;
		var capacityUnits = dataLayer.capacityUnits;
		var popupContent, tooltipContent;

		var options = {"maxWidth": 600};

		var tooltipOptions = {"offset": [0,200]};

		// Assign HTML content to use in a popup when the user clicks on a marker
		//
		if ( dataLayer.dataType == "fitAssetsGeoJSON" ){  
			const latitude = (+feature.geometry.coordinates[1]).toFixed(5);
			const longitude = (+feature.geometry.coordinates[0]).toFixed(5);
			const capacityString = feature.properties.floodImpactSummary[capacity] == null ? "unknown" : feature.properties.floodImpactSummary[capacity] + " " + feature.properties.floodImpactSummary[capacityUnits];
			popupContent = "<p style='font-size:20px'>" + 
				"Asset name: " + feature.properties.floodImpactSummary[stationName] + 
				"<br>Asset id: " + feature.properties.floodImpactSummary[stationID] +
				"<br>Asset capacity: " + capacityString +
				"<br><img src='https://maps.googleapis.com/maps/api/streetview?size=320x240&location=" + latitude + "," + longitude + "&fov=120&heading=235&pitch=10&sensor=false&key=AIzaSyBK3hDxsLa-Mo-KGHdLKZUzOwY9BnGWAR0'>" +
				"<br><a href='http://maps.google.com/maps?q=&layer=c&cbll=" + latitude + "," + longitude + "' target='_blank'>Click to view in Google Street View.</a>" +
				"</p> <button class=\"popupButton\" onclick=\"globalMapComponent.gotoFloodImpactDetail(\'" + feature.properties.floodImpactSummary[stationID] + "\')\">Go to Impact Summary</button>";
			options = { maxWidth: 320 }; // was minWidth
			
			tooltipContent = feature.properties.floodImpactSummary.tooltipContent;
		}
		// everything else
		else { 
			// first, create popup content
			if ( feature.properties.stationSummary.hasOwnProperty('popupContent')) {
				popupContent = feature.properties.stationSummary.popupContent;
			} else {
				popupContent = "<p style='font-size:20px'>" + 
					"Station name: " + feature.properties.stationSummary[stationName] + 
					"<br>Station id: " + feature.properties.stationSummary[stationID] +
					"</p> <button class=\"popupButton\" onclick='globalMapComponent.gotoDetail(" + feature.properties.stationSummary[stationID] +
					")'>Go to Station Dashboard</button>";
			}
				
			// next, assign tool tip content
			tooltipContent = feature.properties.stationSummary.tooltipContent;
			
		}

		if (feature.properties && feature.properties.popupContent) {
			// This will append content from a "popupContent" property if it is in the GeoJSON file
			popupContent += feature.properties.popupContent;
		}

		// check location of marker, set tooltip accordingly
		/* https://gis.stackexchange.com/questions/394960/marker-position-in-leaflet
		*/
		/*
		const container = self.map.getContainer();
		const point = self.map.latLngToContainerPoint(
			{lat: feature.properties.stationSummary.latitude,
			 lng: feature.properties.stationSummary.longitude});
		if ( point.x < (container.clientWidth / 2)) {
			tooltipOptions = tooltipOptionsLeft;
		}
		*/
		
		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent, tooltipOptions);

		// https://gis.stackexchange.com/questions/358804/dynamic-offset-of-leaflet-popup
		// layer.on('mouseenter', function() { 
			// self.setTooltipOffset
		// }, layer.getTooltip()) ;
		// layer.tooltipopen ( ev ) {}
	}	

	//
	// placeMarkers controls the marker placement, title, and color.
	//
	convertScoreToTextColor ( score ) {
		switch ( score ) { 
			case -1: return "white"; break;
			case 0: return "white"; break;
			case 14:
			case 6:
			case 7:
			case 1: return "white"; break;
			case 15:
			case 8:
			case 2: return "black"; break;
			case 16:
			case 9:
			case 3: return "black"; break;
			case 17:
			case 10:
			case 4: return "black"; break;
			case 18:
			case 11:
			case 12:
			case 13:
			case 5: return "white"; break;
			default: return "black"; break;
		}
	}

	convertScoreToColor ( score ) {
		switch ( score ) { 
		case -1: return "blue-dark"; break;
		case 0: return "black"; break;
		case 14:
		case 6:
		case 7:
		case 1: return "#008A36"; break;		// green
		case 15:
		case 8:
		case 2: return "#F5B831"; break;		// yellow
		case 16:
		case 9:
		case 3: return "#EE8818"; break;		// orange
		case 17:
		case 10:
		case 4: return "#B63D91"; break;	// magenta
		case 18:
		case 11:
		case 12:
		case 13:
		case 5: return "#D12619"; break;	// red
		default: return "white"; break;
		}
	}

	// this is now available because of lambda function definition
	placeMarkers = (feature, latlng) => { 
		var marker;
		// console.log(this);  
		// console.log ( feature );

		if ( feature.dataType == "fitAssetsGeoJSON" ) {
			//console.log(feature);
			var novaScore = feature.properties.floodImpactSummary[feature.novaScoreString];
			var color = this.convertScoreToColor( novaScore );
			var textColor = this.convertScoreToTextColor( novaScore );
			var value = feature.properties.floodImpactSummary[feature.titleString];
			var title;
			if ( value == null ) 
				title = "NR";
			else {
				value = +value;
				if ( value > 99 )
					title = value.toFixed(0).toString();
				else if ( value < 1 )
					title = value.toFixed(2).toString();
				else title = value.toFixed(1).toString();
			}
			marker = this.makeMarker( title, title, color, textColor, "");
			marker.options.shadowSize = [50,75];
		}
		else {
			//console.log(feature);
			var novaScore = feature.properties.stationSummary[feature.novaScoreString];
			var color = this.convertScoreToColor( novaScore );
			var textColor = this.convertScoreToTextColor( novaScore );
			var value = feature.properties.stationSummary[feature.titleString];
			var alarms = feature.properties.stationSummary['alarms'];
			var alarmClasses = '';
			var change = feature.properties.stationSummary?.change;

			/* Indicate if this station is in an alarm status */
			if ( alarms.length > 0 ) {
				alarms.forEach ( alarm => {
					if ( alarm.alarmState === 'Active' ) {
						alarmClasses = 'alarm-icon blinking';
					}
				});
			}

			var title;
			if ( novaScore == null ) {
				// This is a special case added for precip values of 0 to avoid clutter.
					marker = L.icon({
						iconUrl: 'assets/images/icon-png-1circle-green.png',
						iconSize: [20, 20],
						iconAnchor: [10, 10]
					});
			} else if ( value === null || value === 'NR' || value === 'OOS') {
				if ( value === 'OOS' ) {
					marker = L.icon({
						iconUrl: 'assets/images/icon-png-1circle-blue.png',
						iconSize: [20, 20],
						iconAnchor: [10, 10]
					});
				} else {
					marker = L.icon({
						iconUrl: 'assets/images/icon-png-1circle-black.png',
						iconSize: [20, 20],
						iconAnchor: [10, 10]
					});
				}
			} else {
				title = value;
				let titleForWidth = value;
				// for when we are ready to display indicators in the markers
				if ( change != undefined ) {
					title =  change + " " + value;
					// title = "&#8657 &#8660 " + value + " &#8607" + "&#8648";
					titleForWidth = "X " + value;
				}
				marker = this.makeMarker( title, titleForWidth, color, textColor, alarmClasses);
				marker.options.shadowSize = [50,75];
			}
		}

		return L.marker(latlng, {icon: marker});  // http://stackoverflow.com/questions/29080194/leaflet-adding-an-offset-to-marker
	}

	// Create a custom svg marker containing the textString, using the specified color
	// and specified text color.  Additional class such as alarm classes, 
	// separated by a space, can be passed.
	makeMarker( textString: string, textStringForWidth: string, color: string, textColor: string, classes: string) {
		const bbox = this.bboxText(textStringForWidth);
		const bufferX = 5;
		const bufferY = 3;

		// width measurements
		const textWidth = bbox.width;
		const svgWidth = textWidth + (2*bufferX);
		const centerX = svgWidth/2.0;

		const textHeight = bbox.height;
		const rectHeight = textHeight + (2*bufferY);
		const textY = (rectHeight / 2) + bufferY;
		const tailHeight = textHeight;
		const svgHeight = rectHeight + tailHeight;

		const svgString = 
			"<svg xmlns='http://www/w3.org/2000/svg' version='1.1' width='" + svgWidth + "' height='" + svgHeight + "'>" +
			"<rect x='0'  y='0' width='" + svgWidth + "' height='" + rectHeight + "' rx='5' ry='5' fill='" + color + " ' />" +
			"<polygon points='" + (centerX+5) + "," + (rectHeight-1) + " " + centerX + "," + 
				svgHeight + " " + (centerX-5) + "," + (rectHeight-1) + "' fill='" + color + "' />" +
			"<text class='textOnMarker " + classes + "' x='" + bufferX + "' y='" + textY + 
				"' stroke='" + textColor + "' fill='" + textColor + "'>" +
			"<tspan>" + textString + "</tspan></text>" +
			"</svg>";
		const iconUrl = 'data:image/svg+xml,' + svgString;
				
		const marker = L.divIcon({
				iconSize: new L.Point(svgWidth, svgHeight),
				iconAnchor: [centerX, svgHeight],
				html: svgString
		});

		return marker;
	}

	// return a bouding box for the given string, such as:
	// {x: 0, y: -12.800000190734863, width: 27.25, height: 16}
	bboxText ( textString: string, fontFamily: string = "Helvetica neue, Helvetica, Arial, sans-serif", fontSize: string = "14px"): SVGRect {

		const svgNS = "http://www.w3.org/2000/svg";

		let svg = document.createElementNS(svgNS, "svg");
		svg.setAttribute("xmlns", svgNS);
		svg.setAttribute("width", "0");
		svg.setAttribute("height", "0");
		svg.style.position = "absolute";
  		svg.style.top = "-9999px";
  		svg.style.left = "-9999px";

		let textElement = document.createElementNS( svgNS, "text");
		textElement.style.fontSize = fontSize;
		textElement.style.fontFamily = fontFamily;
		textElement.textContent = textString;
		svg.appendChild(textElement);
		document.body.appendChild(svg);
		const bbox = textElement.getBBox();
		svg.remove();
		return bbox;
	}


	// Because mapService is the communications hub between components (map and additionLegends in this case), the list
	// of additional legends must be maintained here.  The dataLayerLegendDisplay variable only has a unique id (assigned
	// in the configuration file), a variable indicating whether the legend should be hidden or not, and an observable
	// associated with that variable.
	createNewDataLayerLegendDisplay ( id ) {
		const newDataLayerLegendDisplay = {};
		newDataLayerLegendDisplay['id'] = id;
		newDataLayerLegendDisplay['display'] = false;  // added just so we can accurately count the numer of legends active below in calculateDataLegendCount
		newDataLayerLegendDisplay['source'] = new Subject<boolean>();
		newDataLayerLegendDisplay['observable'] = newDataLayerLegendDisplay['source'].asObservable();
		this.dataLayerLegendDisplay.push ( newDataLayerLegendDisplay );

	}

	// Tabulate the number of overlay legends currently displayed.  Set the obsevable to that value so followers can respond.
	// Currently, if 0, the single overlay dialog is set to hide.
	calculateDataLayerLegendCount () {
		let count = 0;
		for ( let i = 0; i < this.dataLayerLegendDisplay.length; i++ ) {
			if ( this.dataLayerLegendDisplay[i].display == true ) {
				count++;
			}
		}
		this.dataLayerLegendCount = count;
		this.dataLayerLegendCountObservable.next(this.dataLayerLegendCount);
	}

	// get the dataLayerLegend object associated with a particular id
	getDataLayerLegendDisplay ( id ) {
		return this.dataLayerLegendDisplay.filter(dl => dl.id === id)[0];
	}

	// deactivateLayer both removes the layer from the map and sets the active flag to false.
	// This routine can deactivate both a point data layer as well as a polygon layer.
	deactivateLayer(dataType: string) {
		// console.log(dataType);
		const dataLayer = this.dataLayerService.getDataLayerDetails(dataType);
		if ( dataLayer.active === true ) {
			if ( this.map != null ) {
				this.map.removeLayer(dataLayer.mapLayer);
			}
			dataLayer.active = false;
		}
	}


	// Polygon layers may need to be removed from the map to avoid memory leaks when changing
	// from one route to another (map to station summary, for example)
	// but the layer should still be allowed to be "active" to refresh.
	removeLayer(dataType: string) {
		// console.log(dataType);
		const dataLayer = this.dataLayerService.getDataLayerDetails(dataType);
		if ( dataLayer.active === true ) {
			this.map.removeLayer(dataLayer.mapLayer);
		}
	}

	// zoom map into bounding box specified by the LatLngBounds
	fitBoundsWithBounds (bounds: any) {
		this.map.fitBounds(bounds);
	}

	// zoom map using the lat/lng pairs separated by ","  i.e:
	// "-104.8612631,39.37094832,-104.8612631,39.37094832"
	fitBounds( ) {
		// console.log("zoom to " + this.zoomSelection);

		if ( this.zoomSelection == 'default') {
			this.setZoomAroundDefault();
		} else if ( this.zoomSelection == 'user') {
			this.setZoomAroundUserPreferences();
		} else {
			const res = this.zoomSelection.split(',');
			if ( res.length == 4 ) {
				// boundsString format must be good.  Continue to set latLngBounds and zoom appropriately.
				const southWest =  L.latLng(+res[1], +res[0]);
				const northEast =  L.latLng(+res[3], +res[2]);
				const bounds =  L.latLngBounds(southWest, northEast);
				this.fitBoundsWithBounds ( bounds );
			}
		}
	}

	// resets the map center and zoom level to default values defined above
	setZoomAroundDefault ( ) {
		const cf = this.configService.getConfigFilePreferences();
		// console.log(cf);
		this.map.setView ( 
			new L.LatLng(+cf.map.latitude, +cf.map.longitude), 
			+cf.map.zoom);
	}

	// resets the map center and zoom level to user preferences
	setZoomAroundUserPreferences ( ) {
		// console.log(this.userPreferences.preferences.map.longitude);
		if ( this.userPreferences != null ) {
			const lat = +this.userPreferences.preferences.map.latitude;
			const lon =	+this.userPreferences.preferences.map.longitude;
			const zoom = +this.userPreferences.preferences.map.zoom;
			console.log( lat, lon, zoom );
			if ( this.map != null ) {
				this.map.setView ( new L.LatLng( lat, lon), zoom );
			}
			this.setCurrentCenterWithLatLon( lat, lon );
			this.setCurrentZoom ( zoom );
		}
	}

	// Redraw the map using current center and zoom level
	redrawMap ( ) {
		if ( this.map != null ) {
			this.map.setView ( this.currentCenter, +this.currentZoom);
			return true;
		}
		return false;
	}

	// Keep track of the active TileLayer.  Use this information on refresh of map.
	setActiveTileLayer (activeTileLayer: TileLayer) {
		// console.log(activeTileLayer);  // can i do that?
		this.activeTileLayer = activeTileLayer;
		if ( this.map != null ) {
			this.activeTileLayer.addTo(this.map).bringToBack();
		}
	}

	getActiveTileLayerName () {
		// tslint:disable-next-line:max-line-length
		// console.log(Object.keys(this.baseMaps)); // ["GoogleSatellite", "OpenStreetMap", "Esri", "CartoDB", "GoogleStreet", "OfflineOpenStreetMap"]
		for ( const i in this.baseMaps ) {
			// console.log( this.baseMaps[i]] );
			if ( this.baseMaps[i] === this.activeTileLayer ) {
				return(i);
			}
		}
		return 'unknown';
	}

	setCurrentCenter ( centerLatLng ) {
		this.currentCenter = centerLatLng;
	}

	setCurrentCenterWithLatLon( lat, lon ) {
		this.setCurrentCenter((new L.LatLng(+lat, +lon)));
	}

	getCurrentCenter () {
		return this.currentCenter;
	}

	getCurrentLat() {
		return this.currentCenter.lat;
	}

	getCurrentLng() {
		return this.currentCenter.lng;
	}

	setCurrentZoom ( currentZoom ) {
		// console.log('In setCurrentZoom: ' + currentZoom );
		this.currentZoom = currentZoom;
	}

	getCurrentZoom () {
		return this.currentZoom;
	}

	// This routine doesn't actually turn the legend on and off but tracks the state of the legend for refresh.
	toggleShowLegend () {
		this.showLegend = (this.showLegend === true ? false : true);
		this.dataLayerService.refreshLegendList();
	}
	getShowLegend(): boolean {
		return this.showLegend;
	}

	// This routine toggles the legend menu on and off
	toggleLegendMain () {
		var menu = document.getElementById("legendMenu");
		if ( menu != null ) {
			var display = menu.style.display;
			menu.style.display = (display == "none" ) ? "block" : "none";
		}
	}

	disableMouseEvent(tag: string) {
		const html = L.DomUtil.get(tag);

		L.DomEvent.disableClickPropagation(html);
		L.DomEvent.on(html, 'mousewheel', L.DomEvent.stopPropagation);
	}
}
